/**
 * AdminTabPanels — obszar treści powłoki admina z kartami (wzorzec SellCall `TabPanels`).
 *
 * Analogicznie do `TenantTabPanels`: renderuje WSZYSTKIE otwarte karty naraz (keep-alive,
 * `display` toggle, lazy-mount), każda z własnym `<Routes location={tabPath}>`.
 *
 * Strony admina renderują własne `<Layout>` — w powłoce kart (kontekst `TabShellContext=true`
 * z `AdminShell`) `Layout` renderuje tylko `<main>`, więc chrome nie dubluje się per karta.
 *
 * Tabela tras = chronione trasy admina (przeniesione z `App.tsx`). Pre-login oraz pełnoekranowy
 * builder wzoru procesu (`/process-templates/builder/:id`) żyją poza powłoką (w `App.tsx`).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useTabs } from '@/contexts/TabContext';
import { AdminMenuBuilderPage } from '@/pages/admin-menu-builder/AdminMenuBuilderPage';
import { AdminProcessTemplatesPage } from '@/pages/admin-process-templates/AdminProcessTemplatesPage';
import { AdminAuditLog } from '@/pages/AdminAuditLog';
import { AdminBugReports } from '@/pages/AdminBugReports';
import { AdminBundleEdit } from '@/pages/AdminBundleEdit';
import { AdminBundles } from '@/pages/AdminBundles';
import { AdminChartOfAccountsTemplates } from '@/pages/AdminChartOfAccountsTemplates';
import { AdminCoupons } from '@/pages/AdminCoupons';
import { AdminDbPools } from '@/pages/AdminDbPools';
import { AdminFiscalAgentReleases } from '@/pages/AdminFiscalAgentReleases';
import { AdminGlobalDictionaries } from '@/pages/AdminGlobalDictionaries';
import { AdminGlobalDocumentTypes } from '@/pages/AdminGlobalDocumentTypes';
import { AdminInvoices } from '@/pages/AdminInvoices';
import { AdminKartoteki } from '@/pages/AdminKartoteki';
import { AdminModulePricing } from '@/pages/AdminModulePricing';
import { AdminModules } from '@/pages/AdminModules';
import { AdminProfile } from '@/pages/AdminProfile';
import { AdminRoles } from '@/pages/AdminRoles';
import { AdminSubscriptions } from '@/pages/AdminSubscriptions';
import { AdminTenantDatabase } from '@/pages/AdminTenantDatabase';
import { AdminTenantDetail } from '@/pages/AdminTenantDetail';
import { AdminTenants } from '@/pages/AdminTenants';
import { AdminUsers } from '@/pages/AdminUsers';
import { Dashboard } from '@/pages/Dashboard';
import { ModuleTemplateEditor } from '@/pages/ModuleTemplateEditor';
import { ModuleTemplates } from '@/pages/ModuleTemplates';
import { TemplateFormBuilderPage } from '@/pages/TemplateFormBuilderPage';
import { TemplateListBuilderPage } from '@/pages/TemplateListBuilderPage';
import { TemplateMenuBuilderPage } from '@/pages/TemplateMenuBuilderPage';

/** Parsuje string ścieżki na obiekt Location dla `<Routes location>`. */
function parseLocation(path: string) {
  const [pathname, search] = path.split('?');
  return {
    pathname: pathname || '/',
    search: search ? `?${search}` : '',
    hash: '',
    state: null,
    key: 'default',
  };
}

function TabNotFound() {
  const { t } = useTranslation();
  return (
    <div className="flex h-64 items-center justify-center text-text-tertiary">
      <p className="text-lg">{t('tabs.notFound')}</p>
    </div>
  );
}

/** Tabela tras admina — renderowana per karta z jej własnym `location`. */
function AdminRoutes({ location }: { location: ReturnType<typeof parseLocation> }) {
  return (
    <Routes location={location}>
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/modules" element={<AdminModules />} />
      <Route path="/module-pricing" element={<AdminModulePricing />} />
      <Route path="/bundles" element={<AdminBundles />} />
      <Route path="/bundles/:code" element={<AdminBundleEdit />} />
      <Route path="/db-pools" element={<AdminDbPools />} />
      <Route path="/tenants" element={<AdminTenants />} />
      <Route path="/tenants/:tenantId" element={<AdminTenantDetail />} />
      <Route path="/tenants/:tenantId/database" element={<AdminTenantDatabase />} />
      <Route path="/subscriptions" element={<AdminSubscriptions />} />
      <Route path="/invoices" element={<AdminInvoices />} />
      <Route path="/coupons" element={<AdminCoupons />} />
      <Route path="/admin-users" element={<AdminUsers />} />
      <Route path="/admin-roles" element={<AdminRoles />} />
      <Route path="/kartoteki" element={<AdminKartoteki />} />
      <Route path="/global-document-types" element={<AdminGlobalDocumentTypes />} />
      <Route path="/global-dictionaries" element={<AdminGlobalDictionaries />} />
      <Route path="/chart-of-accounts-templates" element={<AdminChartOfAccountsTemplates />} />
      <Route path="/fiscal-agent-releases" element={<AdminFiscalAgentReleases />} />
      <Route path="/module-areas" element={<Navigate to="/module-templates" replace />} />
      <Route path="/module-templates" element={<ModuleTemplates />} />
      <Route path="/module-templates/:slug" element={<ModuleTemplateEditor />} />
      <Route
        path="/module-templates/:slug/entities/:entitySlug/views/:viewSlug/form-builder"
        element={<TemplateFormBuilderPage />}
      />
      <Route
        path="/module-templates/:slug/entities/:entitySlug/views/:viewSlug/list-builder"
        element={<TemplateListBuilderPage />}
      />
      <Route path="/module-templates/:slug/menu-builder" element={<TemplateMenuBuilderPage />} />
      <Route path="/audit-log" element={<AdminAuditLog />} />
      <Route path="/bug-reports" element={<AdminBugReports />} />
      <Route path="/menu-builder" element={<AdminMenuBuilderPage />} />
      <Route path="/profile" element={<AdminProfile />} />
      <Route path="/process-templates" element={<AdminProcessTemplatesPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<TabNotFound />} />
    </Routes>
  );
}

export function AdminTabPanels() {
  const { tabs, activeTabId } = useTabs();

  // Lazy-mount: drzewo karty montuje się dopiero przy pierwszej aktywacji.
  const [mountedTabIds, setMountedTabIds] = useState<Set<string>>(() =>
    activeTabId ? new Set([activeTabId]) : new Set(),
  );

  useEffect(() => {
    if (activeTabId && !mountedTabIds.has(activeTabId)) {
      setMountedTabIds((prev) => new Set([...prev, activeTabId]));
    }
  }, [activeTabId, mountedTabIds]);

  // Sprzątanie: usuń zamknięte karty z zamontowanych.
  useEffect(() => {
    const ids = new Set(tabs.map((t) => t.id));
    setMountedTabIds((prev) => {
      const updated = new Set([...prev].filter((id) => ids.has(id)));
      return updated.size !== prev.size ? updated : prev;
    });
  }, [tabs]);

  return (
    <div className="relative h-full w-full overflow-hidden" data-testid="tab-panels">
      {tabs.map((tab) => {
        if (!mountedTabIds.has(tab.id)) return null;
        const isActive = tab.id === activeTabId;
        return (
          <div
            key={tab.id}
            className="absolute inset-0 overflow-auto"
            style={{ display: isActive ? 'block' : 'none' }}
            data-testid={`tab-panel-${tab.basePath}`}
          >
            <AdminRoutes location={parseLocation(tab.path)} />
          </div>
        );
      })}
    </div>
  );
}
